<template>
    <div class="p-6 w-full">
        <div class="mb-6">
            <h1 class="text-3xl font-bold text-rose-800 mb-2">Istoric Consultații</h1>
            <p class="text-gray-600">Vizualizează istoricul complet al consultațiilor pentru fiecare pacient</p>
        </div>

        <!-- Patient Search Section -->
        <div class="bg-white rounded-lg shadow p-6 mb-6">
            <h2 class="text-xl font-semibold mb-4">Selectează Pacient</h2>
            <div class="flex gap-4 items-end">
                <div class="flex-1">
                    <label class="block text-sm font-medium text-gray-700 mb-2">
                        Caută după nume sau CNP
                    </label>
                    <AutoComplete data-cy="patient-search-autocomplete" v-model="searchQuery" :suggestions="filteredPatients" @complete="searchPatients"
                        @item-select="selectPatient" optionLabel="label" placeholder="Introdu nume sau CNP..."
                        class="w-full" :dropdown="true">
                        <template #option="slotProps">
                            <div class="flex justify-between items-center w-full">
                                <div>
                                    <div class="font-semibold">{{ slotProps.option.name }}</div>
                                    <div class="text-sm text-gray-600">CNP: {{ slotProps.option.cnp }}</div>
                                </div>
                                <div class="text-sm text-gray-500">
                                    {{ slotProps.option.age }} ani, {{ slotProps.option.sex }}
                                </div>
                            </div>
                        </template>
                    </AutoComplete>
                </div>
                <Button data-cy="clear-selection-button" v-if="selectedPatientLocal" label="Șterge selecția" icon="pi pi-times" @click="clearSelection"
                    severity="secondary" outlined />
            </div>

            <!-- Selected Patient Info -->
            <div v-if="selectedPatientLocal" class="mt-4 p-4 bg-rose-50 rounded-lg">
                <div class="flex items-center gap-4">
                    <i class="pi pi-user text-3xl text-rose-600"></i>
                    <div class="flex-1">
                        <h3 class="font-bold text-lg">{{ selectedPatientLocal.name }}</h3>
                        <div class="grid grid-cols-3 gap-4 mt-2 text-sm">
                            <div>
                                <span class="text-gray-600">CNP:</span>
                                <span class="ml-2 font-medium">{{ selectedPatientLocal.cnp }}</span>
                            </div>
                            <div>
                                <span class="text-gray-600">Vârstă:</span>
                                <span class="ml-2 font-medium">{{ selectedPatientLocal.age }} ani</span>
                            </div>
                            <div>
                                <span class="text-gray-600">Sex:</span>
                                <span class="ml-2 font-medium">{{ selectedPatientLocal.sex }}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- History Records -->
        <div v-if="loading" class="text-center py-8">
            <i class="pi pi-spin pi-spinner text-4xl text-rose-600"></i>
            <p class="mt-4 text-gray-600">Se încarcă istoricul...</p>
        </div>

        <div v-else-if="!selectedPatientLocal" class="text-center py-12 bg-gray-50 rounded-lg">
            <i class="pi pi-search text-6xl text-gray-400 mb-4"></i>
            <p class="text-xl text-gray-600">Selectează un pacient pentru a vedea istoricul</p>
        </div>

        <div v-else-if="historyRecords.length === 0" class="text-center py-12 bg-gray-50 rounded-lg">
            <i class="pi pi-inbox text-6xl text-gray-400 mb-4"></i>
            <p class="text-xl text-gray-600">Nu există consultații pentru acest pacient</p>
        </div>

        <div v-else class="bg-white rounded-lg shadow overflow-hidden">
            <div class="bg-gradient-to-r from-rose-700 to-rose-900 text-white p-4">
                <h2 class="text-xl font-semibold">
                    Istoric Consultații - {{ selectedPatientLocal.name }}
                </h2>
                <p class="text-rose-100 text-sm mt-1">
                    {{ historyRecords.length }} {{ historyRecords.length === 1 ? 'consultație înregistrată' :
                        'consultații înregistrate' }}
                </p>
            </div>

            <div class="divide-y">
                <div v-for="(record, index) in historyRecords" :key="record.id"
                    class="p-4 hover:bg-gray-50 transition-colors">
                    <div class="flex items-start justify-between">
                        <div class="flex-1">
                            <div class="flex items-center gap-3 mb-2">
                                <span class="bg-rose-100 text-rose-800 px-3 py-1 rounded-full text-sm font-semibold">
                                    #{{ historyRecords.length - index }}
                                </span>
                                <span class="text-lg font-semibold text-gray-800">
                                    {{ record.dateConsult }}
                                </span>
                                <Badge :value="getExamLabel(record)" :severity="getExamSeverity(record)" class="ml-2" />
                                <span class="text-sm text-gray-500">
                                    Salvat: {{ formatDate(record.updatedAt) }}
                                </span>
                            </div>
                            <div class="flex items-center gap-2 mb-2">
                                <i class="pi pi-user-md text-blue-600"></i>
                                <span class="text-sm text-gray-700">
                                    Examinat de: <span class="font-medium">{{ record.doctorNameSnapshot || 'N/A'
                                    }}</span>
                                </span>
                            </div>

                            <!-- Expandable Details -->
                            <div class="mt-3">
                                <button @click="toggleExpand(record.id)"
                                    class="text-rose-600 hover:text-rose-800 font-medium text-sm flex items-center gap-1">
                                    <i
                                        :class="expandedRecords.has(record.id) ? 'pi pi-chevron-up' : 'pi pi-chevron-down'"></i>
                                    {{ expandedRecords.has(record.id) ? 'Ascunde detalii' : 'Vezi detalii' }}
                                </button>

                                <div v-if="expandedRecords.has(record.id)" class="mt-3 grid grid-cols-3 gap-4 text-sm">
                                    <div class="bg-gray-50 p-3 rounded">
                                        <h4 class="font-semibold text-gray-700 mb-2">Date Pacient</h4>
                                        <div class="space-y-1">
                                            <div><span class="text-gray-600">Înălțime:</span> <span
                                                    class="font-medium">{{
                                                        selectedPatientLocal.height
                                                    }} cm</span></div>
                                            <div><span class="text-gray-600">Greutate:</span> <span
                                                    class="font-medium">{{
                                                        selectedPatientLocal.weight
                                                    }} kg</span></div>
                                            <div><span class="text-gray-600">BSA:</span> <span class="font-medium">{{
                                                selectedPatientLocal.bsa }}
                                                    m²</span></div>
                                        </div>
                                    </div>

                                    <div class="bg-gray-50 p-3 rounded">
                                        <h4 class="font-semibold text-gray-700 mb-2">Ventricul Stâng</h4>
                                        <div class="space-y-1">
                                            <div><span class="text-gray-600">DTDVS:</span> <span class="font-medium">{{
                                                record.DTDVS || '-' }}
                                                    mm</span></div>
                                            <div><span class="text-gray-600">DTSVS:</span> <span class="font-medium">{{
                                                record.DTSVS || '-' }}
                                                    mm</span></div>
                                            <div><span class="text-gray-600">FE:</span> <span class="font-medium">{{
                                                record.FE
                                                || '-'
                                                    }}%</span>
                                            </div>
                                            <div><span class="text-gray-600">MAPSE:</span> <span class="font-medium">{{
                                                record.MAPSE || '-' }}
                                                    mm</span></div>
                                        </div>
                                    </div>

                                    <div class="bg-gray-50 p-3 rounded">
                                        <h4 class="font-semibold text-gray-700 mb-2">Volum & Masă</h4>
                                        <div class="space-y-1">
                                            <div><span class="text-gray-600">VTDVS:</span> <span class="font-medium">{{
                                                record.VTDVS || '-' }}
                                                    ml</span></div>
                                            <div><span class="text-gray-600">VTSVS:</span> <span class="font-medium">{{
                                                record.VTSVS || '-' }}
                                                    ml</span></div>
                                            <div><span class="text-gray-600">Masă VS:</span> <span
                                                    class="font-medium">{{
                                                        record.MasaVS || '-'
                                                    }}
                                                    g/m²</span></div>
                                        </div>
                                    </div>

                                    <div v-if="record.concluzii" class="col-span-3 bg-blue-50 p-3 rounded">
                                        <h4 class="font-semibold text-gray-700 mb-2">Concluzii</h4>
                                        <div class="text-gray-800" v-html="record.concluzii.replace(/\n/g, '<br/>')">
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="flex">
                            <Button v-if="record.status !== 'void'" icon="pi pi-eye" @click="openRecord(record)"
                                severity="info" size="small" v-tooltip="'Vezi buletin complet'" rounded />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { useRouter } from 'vue-router';

import AutoComplete from 'primevue/autocomplete';
import Badge from 'primevue/badge';
import Button from 'primevue/button';

import { db } from '../../firebase/config.js';
import { collection, query, where } from 'firebase/firestore';
import { useCollection } from 'vuefire';
import { flattenExam } from '../../composables/echo/useExamHelpers.js';

import { selectedPatient, setSelectedPatient, clearSelectedPatient } from '../../stores/selectedPatient.js';

const router = useRouter();

const searchQuery = ref('');
const filteredPatients = ref([]);
const selectedPatientLocal = ref(null);
const expandedRecords = ref(new Set());

//fetch all patients from Firestore
const patientsRef = collection(db, 'patients');
const patientsData = useCollection(patientsRef);

// Compute patients list with all data
const allPatients = computed(() => {
    if (!patientsData.value) return []

    return patientsData.value
        .map(patient => {
            // Calculate age from birthDate
            let age = '-';
            if (patient.birthDate) {
                const birthDate = new Date(patient.birthDate);
                const today = new Date();
                age = today.getFullYear() - birthDate.getFullYear();
                const monthDiff = today.getMonth() - birthDate.getMonth();
                if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
                    age--;
                }
            }

            return {
                cnp: patient.cnp,
                lastName: patient.lastName,
                firstName: patient.firstName,
                name: `${patient.lastName} ${patient.firstName}`,
                age: age,
                birthDate: patient.birthDate,
                sex: patient.sex,
                phone: patient.phone,
                notes: patient.notes,
                height: patient.height,
                weight: patient.weight,
                bsa: patient.bsa,
                label: `${patient.lastName} ${patient.firstName} - ${patient.cnp}`
            }
        }).sort((a, b) => a.name.localeCompare(b.name))
})

// Reactive query for selected patient's history
const selectedCNP = ref(null);
const examsRef = collection(db, 'exams');

const historyQuery = computed(() => {
    if (!selectedCNP.value) return null
    return query(examsRef, where('patientId', '==', selectedCNP.value))
})

const historyData = useCollection(historyQuery)

// Sorted history records
const historyRecords = computed(() => {
    if (!historyData.value) return []

    return [...historyData.value]
        .map((exam) => {
            const flat = flattenExam(exam);
            return {
                ...flat,
                id: exam.id,
                status: exam.status || flat.status || 'draft',
                locked: !!(exam.locked ?? flat.locked),
                updatedAt: exam.updatedAt || flat.updatedAt,
                doctorNameSnapshot: exam.doctorNameSnapshot || flat.doctorNameSnapshot,
                dateConsult: exam.dateConsult || flat.dateConsult,
            };
        })
        .sort((a, b) => {
            const dateA = new Date(a.updatedAt || a.dateConsult);
            const dateB = new Date(b.updatedAt || b.dateConsult);
            return dateB - dateA;
        });
})

const loading = computed(() => selectedCNP.value && !historyData.value)

const searchPatients = (event) => {
    const q = (event.query || "").toLowerCase();

    filteredPatients.value = allPatients.value
        .filter(
            (p) => p.name.toLowerCase().includes(q) || (p.cnp || "").includes(q)
        );
}

const selectPatient = (event) => {
    selectedPatientLocal.value = event.value;
    selectedCNP.value = event.value.cnp;

    // keep global selectedPatient store in sync
    setSelectedPatient(event.value);
}

const clearSelection = () => {
    selectedPatientLocal.value = null;
    selectedCNP.value = null;
    searchQuery.value = null;
    expandedRecords.value.clear();
    clearSelectedPatient();
}

const toggleExpand = (recordId) => {
    if (expandedRecords.value.has(recordId)) {
        expandedRecords.value.delete(recordId)
    } else {
        expandedRecords.value.add(recordId)
    }
}

const openRecord = (record) => {
    if (record.status === 'void') return;

    // combine echography data with patient data
    const fullRecord = {
        ...record,
        status: record.status || 'draft',
        locked: record.locked,
        lastNamePacient: selectedPatientLocal.value?.lastName,
        firstNamePacient: selectedPatientLocal.value?.firstName,
        birthDatePacient: selectedPatientLocal.value?.birthDate,
        sexPacient: selectedPatientLocal.value?.sex,
        phonePacient: selectedPatientLocal.value?.phone,
        notesPacient: selectedPatientLocal.value?.notes,
        inaltimePacient: selectedPatientLocal.value?.height,
        greutatePacient: selectedPatientLocal.value?.weight,
        bsaPacient: selectedPatientLocal.value?.bsa
    };

    setSelectedPatient({
        ...fullRecord,
        loadLast: true,
        lastRecord: fullRecord,
        printAfterLoad: false
    });

    router.push({ name: 'formular' });
}

const formatDate = (dateString) => {
    if (!dateString) return '-'
    const date = new Date(dateString)
    return date.toLocaleString('ro-RO', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    })
}

const getExamLabel = (r) => {
    if (r.status === 'void')
        return 'Anulat';
    if (r.locked || r.status === 'final')
        return 'Finalizat';
    return 'Draft';
}

const getExamSeverity = (r) => {
    if (r.status === 'void')
        return 'danger';
    if (r.locked || r.status === 'final')
        return 'success';
    return 'warning';
}

// Watch for initialPatient prop changes (when come from PatientsList to History)
watch(
    () => selectedPatient.value,
    (newPatient) => {
        if (!newPatient) return;

        // Extract CNP from either format
        const cnp = newPatient.cnpPacient || newPatient.cnp
        if (!cnp) return;

        // Create proper patient object for selectedPatient
        selectedPatientLocal.value = {
            cnp,
            lastName: newPatient.lastNamePacient || newPatient.lastName || '',
            firstName: newPatient.firstNamePacient || newPatient.firstName || '',
            name: `${newPatient.lastNamePacient || newPatient.lastName || ''} ${newPatient.firstNamePacient || newPatient.firstName || ''}`.trim(),
            birthDate: newPatient.birthDatePacient || newPatient.birthDate || '',
            sex: newPatient.sexPacient || newPatient.sex || 'F',
            phone: newPatient.phonePacient || newPatient.phone || '',
            notes: newPatient.notesPacient || newPatient.notes || '',
            height: newPatient.inaltimePacient || newPatient.height || '',
            weight: newPatient.greutatePacient || newPatient.weight || '',
            bsa: newPatient.bsaPacient || newPatient.bsa || '',
            label: `${newPatient.lastNamePacient || newPatient.lastName || ''} ${newPatient.firstNamePacient || newPatient.firstName || ''} - ${cnp}`.trim(),
            age: newPatient.age ?? "-",
        };

        // Set searchQuery as the patient object (AutoComplete expects this format)
        searchQuery.value = selectedPatientLocal.value;

        // Trigger reactive query
        selectedCNP.value = cnp
    },
    { immediate: true }
);
</script>
